import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import CountrySearch from "./CountrySearch";
import RegionFilter from "./RegionFilter";

// The canvas carries the country geometry, so it is a separate chunk that loads shortly before it scrolls into view.
const Canvas = lazy(() => import("./GlobalPresenceCanvas"));

/** Fixed canvas sizes — reserved up-front so the lazy chunk arriving never shifts the page. */
const CANVAS = {
  full: "h-[340px] sm:h-[420px] md:h-auto md:aspect-[2.15/1]",
  compact: "h-[300px] sm:h-[380px] md:h-auto md:aspect-[1.85/1]",
};

function Legend() {
  return (
    <ul className="flex items-center gap-x-5 gap-y-1 text-[12px] text-white/60" aria-label="Map legend">
      <li className="flex items-center gap-2"><span className="flex h-3.5 w-3.5 items-center justify-center rounded-[4px] bg-[#1769FF]"><span className="h-1 w-1 rounded-full bg-white" /></span>Shingare presence</li>
      <li className="flex items-center gap-2"><span className="h-3.5 w-3.5 rounded-[4px] border border-[#1d4076] bg-[#0f2b55]" />Other countries</li>
    </ul>
  );
}

/**
 * The map panel: search + legend, the canvas, and region filters. `region` is controlled by the parent so
 * external controls (region cards on the Company page) can drive it.
 * variant "full"    → homepage: search, filters, zoom, large canvas.
 * variant "compact" → Company page: legend + zoom only; the region cards act as the filter.
 */
export default function GlobalPresenceMap({ variant = "full", region, onRegionChange, className = "" }) {
  const full = variant === "full";
  const boxRef = useRef(null);
  const [mounted, setMounted] = useState(false);
  const [selection, setSelection] = useState(null);

  // Mount the canvas (and fetch its chunk) once the panel is within ~one screen of the viewport.
  useEffect(() => {
    const el = boxRef.current;
    if (!el || typeof IntersectionObserver === "undefined") { setMounted(true); return undefined; }
    const io = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) { setMounted(true); io.disconnect(); } }, { rootMargin: "700px 0px" });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const select = useCallback((iso, opts = {}) => setSelection(iso ? { iso, zoom: !!opts.zoom } : null), []);
  const fromSearch = useCallback((iso) => setSelection({ iso, zoom: true }), []);

  return (
    <div className={`overflow-hidden rounded-2xl border border-white/10 bg-[#071B3A] shadow-[0_40px_80px_-40px_rgba(0,0,0,0.75)] ${className}`} data-testid="global-presence-map">
      <div className="relative z-30 flex flex-col gap-3 border-b border-white/[0.07] bg-[#08204A]/70 p-3 sm:flex-row sm:items-center sm:justify-between sm:p-4">
        {full && <CountrySearch onPick={fromSearch} className="w-full sm:max-w-[340px]" />}
        <Legend />
      </div>

      <div ref={boxRef} className={`gp-canvas relative w-full ${CANVAS[variant] || CANVAS.full}`}>
        {mounted && (
          <Suspense fallback={null}>
            <Canvas variant={variant} region={region} onRegionChange={onRegionChange} selection={selection} onSelect={select} />
          </Suspense>
        )}
      </div>

      {full && (
        <div className="border-t border-white/[0.07] bg-[#08204A]/70 p-3 sm:p-4">
          <RegionFilter region={region} onChange={onRegionChange} />
        </div>
      )}
    </div>
  );
}
